import React from "react";
import { useWatch } from "react-hook-form";
import { Box, Text, VStack } from "@chakra-ui/react";

import { useLocale } from "#app/context/LocaleContext.jsx";
import { formatMatchDate } from "#app/utils/dateFormat.js";
import { listDays } from "#app/utils/listDays.js";
import { maybePluralizeNoun } from "#app/utils/words.js";
import {
  getNightLabels,
  looksLikeOneTripsNights,
} from "#app/utils/tripPlannerForm.js";

import { DatePickHint } from "./DatePickHint.jsx";

const LISTED_NIGHTS = 4;

/**
 * Under the trip-planner calendar: a live sentence saying which trips the
 * request will watch, and a hint when the picked days look like the nights
 * of one trip rather than possible start days. Many people pick every night
 * of the trip they have in mind, and each picked day then becomes a separate
 * possible start.
 */
export function TripDatesSummary({ formContext, facility, analyticsProps }) {
  const locale = useLocale();
  const { control, setValue } = formContext;
  const selectedDates = useWatch({ control, name: "selectedDates" }) || [];
  const tripNights = useWatch({ control, name: "tripNights" }) || [];
  const [dismissedDatesKey, setDismissedDatesKey] = React.useState(null);

  const nightCount = tripNights.length;
  const sortedDates = [...selectedDates].sort((a, b) => a - b);
  const datesKey = sortedDates.map((day) => day.getTime()).join(",");

  const showHint =
    looksLikeOneTripsNights(sortedDates, nightCount) &&
    dismissedDatesKey !== datesKey;

  const keepFirstDay = () => {
    setValue("selectedDates", [sortedDates[0]], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <VStack align="stretch" spacing={3} mt={4}>
      {showHint && (
        <DatePickHint
          kind="one-trip-nights"
          analyticsProps={analyticsProps}
          title="Picked every night of your trip?"
          fixLabel={`Keep only ${formatMatchDate(sortedDates[0], locale)}`}
          onFix={keepFirstDay}
          keepLabel="They're all start days"
          onKeep={() => setDismissedDatesKey(datesKey)}
        >
          Each day you pick is a start day. We check the next{" "}
          {nightCount === 2
            ? "night"
            : maybePluralizeNoun(nightCount - 1, "night")}{" "}
          from each one.
        </DatePickHint>
      )}

      <Box data-testid="trip-watch-preview">
        {sortedDates.length === 0 ? (
          <Text fontSize="sm" color="muted">
            Pick a day you could start to see which trips we'll watch.
          </Text>
        ) : (
          <>
            <Text fontSize="sm">
              We'll watch{" "}
              <Text as="strong">
                {nightCount === 1
                  ? "one-night stays"
                  : `${nightCount}-night trips`}
              </Text>{" "}
              starting{" "}
              <Text as="strong">
                {listDays(sortedDates, (day) => formatMatchDate(day, locale))}
              </Text>
              .
            </Text>
            <Text fontSize="sm" color="muted">
              {describeRoute(tripNights, facility.sites)}
            </Text>
            {nightCount > 1 && (
              <Text fontSize="xs" color="muted" mt={1}>
                Shaded days on the calendar are the rest of each trip.
              </Text>
            )}
          </>
        )}
      </Box>
    </VStack>
  );
}

// "Clinton Hut → Mintaro Hut → Dumpling Hut", capped for long routes
function describeRoute(tripNights, sites) {
  const labels = getNightLabels(tripNights, sites).map(
    (label) => label ?? "a site you pick",
  );
  const listed = labels.slice(0, LISTED_NIGHTS).join(" → ");
  const others = labels.length - LISTED_NIGHTS;
  return others > 0
    ? `${listed} → ${maybePluralizeNoun(others, "more night")}`
    : listed;
}
